import { el } from './app-api'

/** Material "fullscreen" / "fullscreen_exit" glyphs as inline SVG — crisp at any DPI and
 *  rendering-reliable across platforms (unlike the ⛶ unicode glyph, which is missing in many
 *  system fonts). Two icons live in the button at once; CSS swaps which is shown by `.is-active`. */
const EXPAND = /* html */ `<svg class="fs-ico fs-ico--expand" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>`
const COMPRESS = /* html */ `<svg class="fs-ico fs-ico--compress" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>`

export interface FullscreenButton {
  el: HTMLButtonElement
  /** Remove the document-level fullscreenchange listener. */
  dispose(): void
}

/** A persistent corner icon-button that toggles browser fullscreen. Reflects the live fullscreen
 *  state (expand ⇄ compress icon) and hides itself entirely where the Fullscreen API is unavailable
 *  (e.g. iPhone Safari), so players never see a dead control. */
export function createFullscreenButton(onToggle: () => void): FullscreenButton {
  const btn = el('button', {
    class: 'fs-toggle',
    html: EXPAND + COMPRESS,
    attrs: { type: 'button', 'aria-label': 'Toggle fullscreen', title: 'Fullscreen' },
  }) as HTMLButtonElement

  // No element-fullscreen support → don't show a button that can't do anything.
  if (!document.fullscreenEnabled) btn.classList.add('is-hidden')

  btn.addEventListener('click', onToggle)

  const sync = () => {
    const on = !!document.fullscreenElement
    btn.classList.toggle('is-active', on)
    btn.setAttribute('aria-pressed', String(on))
    btn.title = on ? 'Exit fullscreen' : 'Fullscreen'
  }
  sync()
  document.addEventListener('fullscreenchange', sync)

  return {
    el: btn,
    dispose: () => document.removeEventListener('fullscreenchange', sync),
  }
}
